import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  Button,
  StarRating,
  TabStepper,
  TextField,
  TypableDropdown,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';

import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { useState } from 'react';
import componentConstants from '../../../core-utils/messages/componentConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import { radioLegend } from '../../../core-utils/helper-utils/careNavigatorHelper';

export interface EndCaseCareNavModelProps {
  open: boolean;
  handleClose: () => any;
  handleSave: () => any;
  dataState: [any, React.Dispatch<React.SetStateAction<any>>];
  referrals: string[];
  caseTypes: string[];
  update?: boolean;
}

const EndCaseCareNavModel: React.FC<EndCaseCareNavModelProps> = ({
  open,
  handleClose,
  handleSave,
  dataState,
  referrals,
  caseTypes,
  update,
}) => {
  const [activeStep, setActiveStep] = useState<number>(0);

  const useStyles = makeStyles()({
    modal: {
      width: '570px',
      height: activeStep === 0 ? '610px' : '592px',
      background: patientAccessTheme.palette.status.white,
      borderRadius: '8px',
      padding: '24px 24px 24px 32px',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      flexDirection: 'column',
    },
    inputFieldsGrid: {
      width: '500px',
      marginTop: '20px',
      background: '#FBFBFE',
      padding: '16px 20px',
      borderRadius: '8px',
    },
    selectFields: {
      display: 'flex',
      flexDirection: 'row',
    },
    cancelButton: {
      padding: '9px 16px',
      width: '102px',
      height: '36px',
      border: '1px solid ' + patientAccessTheme.palette.primary[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    saveButton: {
      padding: '9px 16px',
      width: '158px',
      height: '36px',
      border: '1px solid ' + patientAccessTheme.palette.grey[500],
      borderRadius: '4px',
      textTransform: 'none',
      fontSize: '12px',
      lineHeight: '12px',
      fontWeight: 500,
    },
    rating: {
      width: '460px',
      height: '130px',
      background: patientAccessTheme.palette.status.white,
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
  });
  const { classes } = useStyles();
  const constants = componentConstants.ORGANISMS.CARE_NAV_MODALS.ENDCASE_MODAL;
  const [assignData, setAssignData] = dataState;

  //State Variables
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  //Functions
  const handleStepOnClick = (index: number) => {
    setActiveStep(index);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Grid container className={classes.modal}>
        <Grid
          container
          style={{ width: '100%' }}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Typography
            fontSize="18px"
            lineHeight="26px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.mediumemphasis}
            margin="4px 0px 0px 0px"
          >
            {constants.TITLE}
          </Typography>
        </Grid>
        <Grid style={{ margin: '20px' }}>
          <TabStepper
            steps={constants.TAB_STEPS}
            activeStep={activeStep}
            handleStepOnClick={handleStepOnClick}
            completedSteps={completedSteps}
            stepRadius="50px"
            stepGap="20px"
            stepWidth="220px"
            stepperStyles={{
              background: patientAccessTheme.palette.status.white,
            }}
            stepStyles={{
              padding: '3px',
              background: patientAccessTheme.palette.background.paper,
            }}
            iconStyles={{ height: '24px', width: '24px' }}
          />
        </Grid>
        {activeStep === 0 && (
          <>
            <Grid className={classes.inputFieldsGrid}>
              <Grid className={classes.selectFields}>
                <Grid style={{ marginRight: '30px' }}>
                  <ReportCreateNewLabelField
                    label={constants.CASE_TYPE}
                    component={
                      <TypableDropdown
                        dropDownOptions={caseTypes}
                        dropDownValues={caseTypes}
                        placeholder={'Case Type'}
                        value={assignData.case_type}
                        handleChange={(e: any) => {
                          const value = e?.target?.value as string;
                          setAssignData((prev: any) => ({
                            ...prev,
                            case_type: value,
                          }));
                        }}
                        handleInputChange={(value) => {
                          setAssignData((prev: any) => ({
                            ...prev,
                            case_type: value,
                          }));
                        }}
                        disabled={false}
                        textFieldPlaceholder="Enter case type (or) select from below"
                        height="36px"
                        width="213px"
                        searchable={true}
                      />
                    }
                  />
                </Grid>
                <Grid>
                  <ReportCreateNewLabelField
                    label={constants.REFERRAL}
                    component={
                      <TypableDropdown
                        dropDownOptions={referrals}
                        dropDownValues={referrals}
                        placeholder={'Referral'}
                        value={assignData.referral}
                        handleChange={(e: any) => {
                          const value = e?.target?.value as string;
                          setAssignData((prev: any) => ({
                            ...prev,
                            referral: value,
                          }));
                        }}
                        handleInputChange={(value) => {
                          setAssignData((prev: any) => ({
                            ...prev,
                            referral: value,
                          }));
                        }}
                        disabled={false}
                        textFieldPlaceholder="Enter referral (or) select from below"
                        height="36px"
                        width="213px"
                        searchable={true}
                      />
                    }
                  />
                </Grid>
              </Grid>
              <Grid className={classes.selectFields}>
                <Grid style={{ marginRight: '30px' }}>
                  <ReportCreateNewLabelField
                    label={constants.SAVINGS_IMPACT}
                    component={
                      <TextField
                        type="number"
                        placeholder={constants.SAVINGS_IMPACT}
                        value={assignData.saving}
                        onChange={(e: any) => {
                          setAssignData((prev: any) => ({
                            ...prev,
                            saving: e.target.value,
                          }));
                        }}
                      />
                    }
                  />
                </Grid>
                <Grid>
                  <ReportCreateNewLabelField
                    label={constants.ROI}
                    component={
                      <TextField
                        type="number"
                        value={assignData.roi}
                        placeholder={constants.ROI}
                        onChange={(e: any) => {
                          setAssignData((prev: any) => ({
                            ...prev,
                            roi: e.target.value,
                          }));
                        }}
                      />
                    }
                  />
                </Grid>
              </Grid>
              <Grid className={classes.selectFields}>
                <Grid>
                  <ReportCreateNewLabelField
                    label={constants.MEMBER_RESPONSIVENESS}
                    component={
                      <Grid container>
                        {constants.RESPONSIVENESS_OPTIONS.map((item: string) =>
                          radioLegend(
                            patientAccessTheme.palette.primary[400] ?? '',
                            item,
                            item === assignData.responsivenes,
                            () => {
                               
                              setAssignData((prev: any) => ({
                                ...prev,
                                responsivenes: item,
                              }));
                            },
                          ),
                        )}
                      </Grid>
                    }
                  />
                </Grid>
              </Grid>
              <Grid className={classes.selectFields}>
                <Grid>
                  <TextField
                    value={assignData.responsivenes_desc}
                    minRows={2}
                    multiline={true}
                    error={false}
                    placeholder={constants.TYPE_HERE}
                    height="85px"
                    width="460px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        responsivenes_desc: e.target.value,
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              style={{ width: '100%', padding: '10px 18px 0px 0px' }}
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              <Grid style={{ marginRight: '12px' }}>
                <Button
                  label={constants.CANCEL}
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={handleClose}
                />
              </Grid>
              <Grid>
                <Button
                  width="158px"
                  label={update ? constants.UPDATE_NEXT : constants.NEXT}
                  variant="contained"
                  className={classes.saveButton}
                  disabled={false}
                  onClick={() => {
                    setCompletedSteps([0]);
                    setActiveStep(1);
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Grid className={classes.inputFieldsGrid}>
              <Grid className={classes.selectFields}>
                <Grid>
                  <Grid className={classes.rating}>
                    <Typography
                      fontSize="16px"
                      lineHeight="25.6px"
                      fontType="semiBold"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      margin="4px 0px 0px 0px"
                    >
                      {constants.RATING}
                    </Typography>
                    <StarRating
                      value={assignData.rating}
                      handleNewValue={(value: number) => {
                        setAssignData((prev: any) => ({
                          ...prev,
                          rating: value,
                        }));
                      }}
                      size={'large'}
                      precision={0.1}
                      color={
                        patientAccessTheme.palette.progressBar?.background4
                      }
                      showRating={
                        <Typography
                          fontSize="22px"
                          lineHeight="35.2px"
                          fontType="semiBold"
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                          margin="4px 0px 0px 0px"
                        >
                          {assignData.rating}
                        </Typography>
                      }
                    />
                  </Grid>
                  <TextField
                    value={assignData.review}
                    minRows={3}
                    multiline={true}
                    error={false}
                    placeholder={constants.FEEDBACK}
                    height="120px"
                    width="460px"
                    inputPropsStyle={{
                      padding: '16px',
                    }}
                    onChange={(e: any) => {
                      setAssignData((prev: any) => ({
                        ...prev,
                        review: e.target.value,
                      }));
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              style={{ width: '100%', marginTop: '30px' }}
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              <Grid style={{ marginRight: '12px' }}>
                <Button
                  label={constants.CANCEL}
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={handleClose}
                />
              </Grid>
              <Grid>
                <Button
                  width="158px"
                  label={update ? constants.UPDATE : constants.SUBMIT}
                  variant="contained"
                  className={classes.saveButton}
                  disabled={false}
                  onClick={() => {
                    setCompletedSteps([0, 1]);
                    handleSave();
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  );
};
export default EndCaseCareNavModel;
